import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, 
  ExternalLink, 
  CheckCircle, 
  AlertCircle, 
  FileText, 
  Users,
  Calendar,
  Plus,
  Edit,
  Trash2,
  Download,
  Upload
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';

interface OrganDonationRecord {
  id: string;
  user_id: string;
  registration_status: 'registered' | 'not_registered' | 'unknown';
  registration_date?: string;
  donation_consent: boolean;
  specific_organs: string[];
  family_notified: boolean;
  family_contact?: string;
  notes?: string;
  donatelife_id?: string;
  created_at: string;
  updated_at: string;
}

const ORGAN_OPTIONS = [
  'Heart',
  'Lungs',
  'Liver',
  'Kidneys',
  'Pancreas',
  'Intestines',
  'Eyes (Corneas)',
  'Skin',
  'Bone',
  'Tendons',
  'Heart Valves',
  'Blood Vessels'
];

export function OrganDonation() {
  const { user } = useAuth();
  const [records, setRecords] = useState<OrganDonationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<OrganDonationRecord | null>(null);
  const [formData, setFormData] = useState({
    registration_status: 'not_registered' as 'registered' | 'not_registered' | 'unknown',
    donation_consent: false,
    specific_organs: [] as string[],
    family_notified: false,
    family_contact: '',
    notes: '',
    donatelife_id: ''
  });

  useEffect(() => {
    if (user) {
      loadOrganDonationRecords();
    }
  }, [user]);

  const loadOrganDonationRecords = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('organ_donation_records')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRecords(data || []);
    } catch (error) {
      console.error('Error loading organ donation records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      const recordData = {
        user_id: user.id,
        ...formData,
        registration_date: formData.registration_status === 'registered' ? new Date().toISOString() : null
      };

      if (editingRecord) {
        const { error } = await supabase
          .from('organ_donation_records')
          .update(recordData)
          .eq('id', editingRecord.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('organ_donation_records')
          .insert(recordData);

        if (error) throw error;
      }

      await loadOrganDonationRecords();
      setShowModal(false);
      setEditingRecord(null);
      resetForm();
    } catch (error) {
      console.error('Error saving organ donation record:', error);
    }
  };

  const handleEdit = (record: OrganDonationRecord) => {
    setEditingRecord(record);
    setFormData({
      registration_status: record.registration_status,
      donation_consent: record.donation_consent,
      specific_organs: record.specific_organs,
      family_notified: record.family_notified,
      family_contact: record.family_contact || '',
      notes: record.notes || '',
      donatelife_id: record.donatelife_id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this organ donation record?')) return;

    try {
      const { error } = await supabase
        .from('organ_donation_records')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadOrganDonationRecords();
    } catch (error) {
      console.error('Error deleting organ donation record:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      registration_status: 'not_registered',
      donation_consent: false,
      specific_organs: [],
      family_notified: false,
      family_contact: '',
      notes: '',
      donatelife_id: ''
    });
  };

  const handleOrganToggle = (organ: string) => {
    setFormData(prev => ({
      ...prev,
      specific_organs: prev.specific_organs.includes(organ)
        ? prev.specific_organs.filter(o => o !== organ)
        : [...prev.specific_organs, organ]
    }));
  };

  const openDonateLife = () => {
    window.open('https://www.donatelife.gov.au/', '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-idecide-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organ donation records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Organ Donation</h1>
              <p className="text-gray-600">
                Record your organ donation wishes and register with DonateLife Australia
              </p>
            </div>
            <Button
              onClick={() => setShowModal(true)}
              className="bg-idecide-primary hover:bg-idecide-primary-hover text-idecide-dark"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Record
            </Button>
          </div>
        </div>

        {/* DonateLife Australia Info */}
        <Card className="mb-8 bg-gradient-to-r from-idecide-primary/10 to-idecide-secondary/10 border-idecide-primary/30">
          <div className="p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Heart className="h-8 w-8 text-idecide-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Register with DonateLife Australia
                </h3>
                <p className="text-gray-600 mb-4">
                  The Australian Organ and Tissue Donation and Transplantation Authority (OTA) manages the 
                  national organ and tissue donation program. Register your decision to help save lives.
                </p>
                <div className="flex space-x-3">
                  <Button
                    onClick={openDonateLife}
                    className="bg-idecide-primary hover:bg-idecide-primary-hover text-idecide-dark"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Visit DonateLife Australia
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowModal(true)}
                    className="border-idecide-primary text-idecide-primary hover:bg-idecide-primary hover:text-idecide-dark"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Record My Wishes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Records List */}
        <div className="space-y-6">
          {records.length === 0 ? (
            <Card className="p-8 text-center">
              <Heart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Organ Donation Records
              </h3>
              <p className="text-gray-600 mb-4">
                Start by recording your organ donation wishes or registering with DonateLife Australia.
              </p>
              <Button
                onClick={() => setShowModal(true)}
                className="bg-idecide-primary hover:bg-idecide-primary-hover text-idecide-dark"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Record
              </Button>
            </Card>
          ) : (
            records.map((record, index) => (
              <motion.div
                key={record.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                          record.registration_status === 'registered'
                            ? 'bg-idecide-success/20 text-idecide-success'
                            : record.registration_status === 'not_registered'
                            ? 'bg-idecide-error/20 text-idecide-error'
                            : 'bg-idecide-warning/20 text-idecide-warning'
                        }`}>
                          {record.registration_status === 'registered' ? 'Registered' : 
                           record.registration_status === 'not_registered' ? 'Not Registered' : 'Unknown'}
                        </div>
                        {record.donation_consent && (
                          <div className="flex items-center text-idecide-success">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            <span className="text-sm font-medium">Consent Given</span>
                          </div>
                        )}
                      </div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Organ Donation Record
                      </h3>

                      {record.registration_date && (
                        <p className="text-sm text-gray-600 mb-2">
                          <Calendar className="h-4 w-4 inline mr-1" />
                          Registered: {new Date(record.registration_date).toLocaleDateString()}
                        </p>
                      )}

                      {record.donatelife_id && (
                        <p className="text-sm text-gray-600 mb-2">
                          <FileText className="h-4 w-4 inline mr-1" />
                          DonateLife ID: {record.donatelife_id}
                        </p>
                      )}

                      {record.specific_organs.length > 0 && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700 mb-1">Specific Organs:</p>
                          <div className="flex flex-wrap gap-2">
                            {record.specific_organs.map(organ => (
                              <span
                                key={organ}
                                className="px-2 py-1 bg-idecide-primary/20 text-idecide-primary text-xs rounded-full"
                              >
                                {organ}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {record.family_notified && (
                        <div className="flex items-center text-idecide-success mb-2">
                          <Users className="h-4 w-4 mr-1" />
                          <span className="text-sm">Family notified</span>
                          {record.family_contact && (
                            <span className="text-sm text-gray-600 ml-2">
                              ({record.family_contact})
                            </span>
                          )}
                        </div>
                      )}

                      {record.notes && (
                        <p className="text-sm text-gray-600 mt-2">
                          <strong>Notes:</strong> {record.notes}
                        </p>
                      )}
                    </div>

                    <div className="flex space-x-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(record)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(record.id)}
                        className="text-idecide-error hover:bg-idecide-error/10 border-idecide-error"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))
          )}
        </div>

        {/* Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingRecord(null);
            resetForm();
          }}
          title={editingRecord ? 'Edit Organ Donation Record' : 'Add Organ Donation Record'}
        >
          <div className="space-y-6">
            {/* Registration Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Registration Status
              </label>
              <select
                value={formData.registration_status}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  registration_status: e.target.value as 'registered' | 'not_registered' | 'unknown' 
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-idecide-primary"
              >
                <option value="not_registered">Not Registered</option>
                <option value="registered">Registered with DonateLife</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>

            {/* DonateLife ID */}
            {formData.registration_status === 'registered' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  DonateLife Registration ID (Optional)
                </label>
                <input
                  type="text"
                  value={formData.donatelife_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, donatelife_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-idecide-primary"
                  placeholder="Enter your DonateLife registration ID"
                />
              </div>
            )}

            {/* Donation Consent */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="donation_consent"
                checked={formData.donation_consent}
                onChange={(e) => setFormData(prev => ({ ...prev, donation_consent: e.target.checked }))}
                className="h-4 w-4 text-idecide-primary focus:ring-idecide-primary border-gray-300 rounded"
              />
              <label htmlFor="donation_consent" className="ml-2 block text-sm text-gray-700">
                I consent to organ and tissue donation
              </label>
            </div>

            {/* Specific Organs */}
            {formData.donation_consent && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Specific Organs/Tissues (Select all that apply)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {ORGAN_OPTIONS.map(organ => (
                    <label key={organ} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.specific_organs.includes(organ)}
                        onChange={() => handleOrganToggle(organ)}
                        className="h-4 w-4 text-idecide-primary focus:ring-idecide-primary border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{organ}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Family Notification */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="family_notified"
                checked={formData.family_notified}
                onChange={(e) => setFormData(prev => ({ ...prev, family_notified: e.target.checked }))}
                className="h-4 w-4 text-idecide-primary focus:ring-idecide-primary border-gray-300 rounded"
              />
              <label htmlFor="family_notified" className="ml-2 block text-sm text-gray-700">
                I have notified my family about my organ donation wishes
              </label>
            </div>

            {/* Family Contact */}
            {formData.family_notified && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Family Contact Person
                </label>
                <input
                  type="text"
                  value={formData.family_contact}
                  onChange={(e) => setFormData(prev => ({ ...prev, family_contact: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-idecide-primary"
                  placeholder="Name and relationship of family contact"
                />
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-idecide-primary"
                placeholder="Any additional notes about your organ donation wishes..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  setEditingRecord(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="bg-idecide-primary hover:bg-idecide-primary-hover text-idecide-dark"
              >
                {editingRecord ? 'Update Record' : 'Save Record'}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}
